import { UploadArchiveResult } from '@/core/domain/archive/Archive/Archive.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Form, Modal, Upload, UploadFile } from 'antd';
import { Inbox } from 'react-feather';
import { Rule } from 'antd/lib/form';
import { useState } from 'react';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  onSubmit: (files: File[]) => Promise<void>;
}

interface FormValues {
  files: UploadFile<UploadArchiveResult>[];
}

const acceptTypes = ['image/png', 'image/jpeg', 'application/pdf'];

export default function FinancialAttachmentModal(props: Props) {
  const fieldRequired = useDefaultEmptyFieldError();

  const [form] = Form.useForm<FormValues>();

  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      setIsLoading(true);

      await props.onSubmit(
        values.files.map((file) => file.originFileObj) as File[],
      );

      handleClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  function normalizeFiles(e: {
    file: UploadFile<UploadArchiveResult>;
    fileList: UploadFile<UploadArchiveResult>[];
  }) {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  }

  function handleClose() {
    form.resetFields();
    props.onClose();
  }

  const uploadValidator: Rule = {
    validator(_, files: UploadFile<UploadArchiveResult>[]) {
      return new Promise((res, rej) => {
        files.forEach((file) => {
          if (file.status === 'error') {
            rej(file.error);
          }
        });

        res(null);
      });
    },
  };

  return (
    <Modal
      open={props.open}
      title="Vincular anexos a conta"
      width={400}
      okText="Enviar anexos"
      onOk={handleSubmit}
      confirmLoading={isLoading}
      onCancel={handleClose}
    >
      <Form form={form}>
        <Form.Item
          name="files"
          getValueFromEvent={normalizeFiles}
          rules={[
            { type: 'array', max: 10, message: 'Máximo de 10 arquivos' },
            fieldRequired,
            uploadValidator,
          ]}
          initialValue={[]}
          valuePropName="fileList"
        >
          <Upload
            disabled={isLoading}
            type="drag"
            listType="text"
            maxCount={10}
            multiple
            accept=""
            customRequest={({ onSuccess, file, onError }) => {
              const fileData = file as File;

              const maxSize = 1048576;

              if (fileData) {
                if (!acceptTypes.includes(fileData.type)) {
                  if (onError) {
                    onError(new Error('Arquivos suportados (PNG, JPG, PDF)'));
                  }
                } else if (fileData.size > maxSize) {
                  if (onError) {
                    onError(new Error('Arquivo deve ter no máximo 1mb'));
                  }
                } else if (onSuccess) {
                  onSuccess(null);
                }
              }
            }}
          >
            <p className="ant-upload-drag-icon">
              <Inbox size={32} />
            </p>
            <p className="ant-upload-text">Clique ou arraste os arquivos</p>
            <p className="ant-upload-hint">
              Upload máximo de 10 arquivos, são permitidos: PNG, JPG e PDF, com
              limite de 1mb.
            </p>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
}
